'use client';

import React, { createContext, useContext, useEffect, useState } from 'react';
import { createClient } from '@/lib/supabase/client';
import { User } from '@supabase/supabase-js';
import { useRouter } from 'next/navigation';
import { trackEvent } from '@/lib/analytics/googleAnalytics';
import { setSentryUser, clearSentryUser } from '@/lib/sentry/config';

interface UserProfile {
  id: string;
  email: string;
  fullName: string | null;
  role: string;
  organizationId: string | null;
  isActive: boolean;
  onboardingCompleted: boolean | null;
}

interface OrganizationMembership {
  id: string;
  organizationId: string;
  userId: string;
  role: string;
  isActive: boolean;
  joinedAt: string;
}

interface Organization {
  id: string;
  name: string;
  slug: string;
  organizationType: string | null;
  isActive: boolean;
  organizationTier?: 'tier_1' | 'tier_2' | 'tier_3';
}

interface AuthContextType {
  user: User | null;
  userProfile: UserProfile | null;
  currentMembership: OrganizationMembership | null;
  currentOrganization: Organization | null;
  loading: boolean;
  authError: string | null;
  signIn: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signUp: (email: string, password: string, fullName: string, organizationId?: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

function isSchemaError(error: any): boolean {
  if (!error) return false;

  if (error.code && typeof error.code === 'string') {
    const errorClass = error.code.substring(0, 2);
    if (errorClass === '42' || errorClass === '08') {
      return true;
    }
    if (errorClass === '23') {
      return false;
    }
  }

  if (error.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /invalid.*syntax/i,
      /type.*does not exist/i,
      /undefined.*column/i,
      /undefined.*table/i,
      /undefined.*function/i,
    ];

    return schemaErrorPatterns.some((pattern) => pattern.test(error.message));
  }

  return false;
}

/**
 * Auto-create an organization for a new user who has no org yet.
 * Returns the new org id or null on failure.
 */
async function createDefaultOrganization(
  supabase: ReturnType<typeof createClient>,
  userId: string,
  userEmail: string,
  fullName: string | null
): Promise<string | null> {
  try {
    const orgName = fullName
      ? `${fullName}'s Organization`
      : `${userEmail.split('@')[0]}'s Organization`;
    const slug = orgName
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
      .substring(0, 50);

    const { data: org, error: orgError } = await supabase
      .from('organizations')
      .insert({
        name: orgName,
        slug: `${slug}-${Date.now()}`,
        organization_type: 'enterprise',
        is_active: true,
        organization_tier: 'tier_1',
      })
      .select('id')
      .single();

    if (orgError || !org) {
      console.error('Failed to create default organization:', orgError?.message);
      return null;
    }

    // Add user as owner member
    const { error: memberError } = await supabase.from('organization_members').insert({
      organization_id: org.id,
      user_id: userId,
      role: 'owner',
      is_active: true,
      joined_at: new Date().toISOString(),
    });

    if (memberError) {
      console.error('Failed to add org member:', memberError.message);
    }

    // Update user profile with org id
    await supabase
      .from('user_profiles')
      .update({ organization_id: org.id, role: 'owner' })
      .eq('id', userId);

    return org.id;
  } catch (err: any) {
    console.error('createDefaultOrganization error:', err.message);
    return null;
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [currentMembership, setCurrentMembership] = useState<OrganizationMembership | null>(null);
  const [currentOrganization, setCurrentOrganization] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);
  const router = useRouter();
  const supabase = createClient();

  const fetchUserProfile = async (userId: string) => {
    try {
      const { data: profile, error: profileError } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (profileError) {
        if (isSchemaError(profileError)) {
          console.error('Schema error fetching profile:', profileError.message);
          throw profileError;
        } else {
          console.log('Profile fetch error:', profileError.message);
          return;
        }
      }

      if (profile) {
        let orgId = profile.organization_id;

        // Auto-create org if user has none
        if (!orgId) {
          const { data: { user: currentUser } } = await supabase.auth.getUser();
          orgId = await createDefaultOrganization(
            supabase,
            userId,
            profile.email || currentUser?.email || '',
            profile.full_name
          );
        }

        setUserProfile({
          id: profile.id,
          email: profile.email,
          fullName: profile.full_name,
          role: profile.role || 'employee',
          organizationId: orgId,
          isActive: profile.is_active,
          onboardingCompleted: profile.onboarding_completed ?? null,
        });

        // Fetch active organization membership
        if (orgId) {
          const { data: membership, error: membershipError } = await supabase
            .from('organization_members')
            .select('*')
            .eq('user_id', userId)
            .eq('organization_id', orgId)
            .eq('is_active', true)
            .single();

          if (membershipError) {
            if (isSchemaError(membershipError)) {
              console.error('Schema error fetching membership:', membershipError.message);
              throw membershipError;
            } else {
              console.log('Membership fetch error:', membershipError.message);
            }
          } else if (membership) {
            setCurrentMembership({
              id: membership.id,
              organizationId: membership.organization_id,
              userId: membership.user_id,
              role: membership.role,
              isActive: membership.is_active,
              joinedAt: membership.joined_at,
            });

            // Fetch organization details
            const { data: org, error: orgError } = await supabase
              .from('organizations')
              .select('*')
              .eq('id', orgId)
              .single();

            if (orgError) {
              if (isSchemaError(orgError)) {
                console.error('Schema error fetching organization:', orgError.message);
                throw orgError;
              } else {
                console.log('Organization fetch error:', orgError.message);
              }
            } else if (org) {
              setCurrentOrganization({
                id: org.id,
                name: org.name,
                slug: org.slug,
                organizationType: org.organization_type,
                isActive: org.is_active,
                organizationTier: org.organization_tier || 'tier_1',
              });

              // Set Sentry user context
              setSentryUser(
                profile.id,
                org.id,
                membership.role,
                org.organization_tier || 'tier_1'
              );
            }
          }
        }
      }
    } catch (error: any) {
      console.error('Error in fetchUserProfile:', error.message);
      throw error;
    }
  };

  useEffect(() => {
    let mounted = true;

    const initializeAuth = async () => {
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();

        if (!mounted) return;

        if (session?.user) {
          setUser(session.user);
          try {
            await fetchUserProfile(session.user.id);
          } catch (e: any) {
            // Profile fetch failed — user is still authenticated
            console.warn('Profile fetch failed during init:', e?.message);
          }
        }
      } catch (error: any) {
        console.error('Auth initialization error:', error.message);
        if (mounted) setAuthError('Failed to initialize authentication. Please refresh the page.');
      } finally {
        if (mounted) setLoading(false);
      }
    };

    initializeAuth();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!mounted) return;

      try {
        if (event === 'INITIAL_SESSION') {
          // Already handled by initializeAuth — skip to avoid double processing
          return;
        }

        if (event === 'TOKEN_REFRESHED') {
          // Session token was refreshed — update user state silently
          if (session?.user) {
            setUser(session.user);
          }
          return;
        }

        if (event === 'SIGNED_OUT' || (!session && event !== 'INITIAL_SESSION')) {
          // Session expired or user signed out — clear state and redirect
          setUser(null);
          setUserProfile(null);
          setCurrentMembership(null);
          setCurrentOrganization(null);
          clearSentryUser();
          if (mounted) setLoading(false);

          // Redirect to login for ALL sign-out scenarios
          if (event === 'SIGNED_OUT') {
            // Explicit sign-out — redirect to login (signOut() also calls router.push('/login')
            // but this handles server-side revocation and token expiry cases)
            router.replace('/login');
          } else {
            // Session expired — redirect with message
            router.replace('/login?reason=session_expired');
          }
          return;
        }

        if (session?.user) {
          setUser(session.user);
          setLoading(true);
          try {
            await fetchUserProfile(session.user.id);
          } catch (e: any) {
            // Profile fetch failed — user is still authenticated
            console.warn('Profile fetch failed on auth change:', e?.message);
          } finally {
            if (mounted) setLoading(false);
          }
        } else {
          setUser(null);
          setUserProfile(null);
          setCurrentMembership(null);
          setCurrentOrganization(null);
          if (mounted) setLoading(false);
        }
      } catch (err: any) {
        console.error('Auth state change error:', err.message);
        if (mounted) {
          setAuthError('Authentication error. Please sign in again.');
          setLoading(false);
        }
      }
    });

    // Session expiry check — runs every 5 minutes
    const SESSION_EXPIRY_MS = 24 * 60 * 60 * 1000; // 24 hours
    const expiryInterval = setInterval(async () => {
      if (!mounted) return;
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          // No active session — redirect to login
          if (mounted) {
            setUser(null);
            setUserProfile(null);
            setCurrentMembership(null);
            setCurrentOrganization(null);
            clearSentryUser();
            router.replace('/login?reason=session_expired');
          }
          return;
        }

        // Check if session is older than 24 hours
        const sessionCreatedAt = session.user?.created_at
          ? new Date(session.user.created_at).getTime()
          : null;
        const expiresAt = session.expires_at ? session.expires_at * 1000 : null;

        if (expiresAt && Date.now() > expiresAt) {
          // Token expired — force sign out and redirect
          await supabase.auth.signOut();
          if (mounted) {
            setUser(null);
            setUserProfile(null);
            setCurrentMembership(null);
            setCurrentOrganization(null);
            clearSentryUser();
            router.replace('/login?reason=session_expired');
          }
        }
      } catch (err: any) {
        console.warn('Session expiry check error:', err.message);
      }
    }, 5 * 60 * 1000); // every 5 minutes

    return () => {
      mounted = false;
      subscription.unsubscribe();
      clearInterval(expiryInterval);
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    try {
      setAuthError(null);
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error during sign in:', error.message);
          throw error;
        } else {
          throw new Error(error.message);
        }
      }

      router.refresh();
    } catch (error: any) {
      console.error('Sign in error:', error.message);
      throw error;
    }
  };

  const signInWithGoogle = async () => {
    try {
      setAuthError(null);
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: 'https://chronomind.ai/auth/callback',
        },
      });
      if (error) throw new Error(error.message);
    } catch (error: any) {
      console.error('Google sign in error:', error.message);
      throw error;
    }
  };

  const signUp = async (
    email: string,
    password: string,
    fullName: string,
    organizationId?: string
  ) => {
    try {
      setAuthError(null);
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
            role: 'employee',
            organization_id: organizationId || null,
          },
          emailRedirectTo: 'https://chronomind.ai/auth/callback',
        },
      });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error during sign up:', error.message);
          throw error;
        } else {
          throw new Error(error.message);
        }
      }

      if (data?.user) {
        const { error: profileError } = await supabase
          .from('user_profiles')
          .upsert(
            {
              id: data.user.id,
              email: email,
              full_name: fullName,
              role: 'employee',
              organization_id: organizationId || null,
              is_active: true,
              onboarding_completed: false,
              onboarding_step: 0,
            },
            { onConflict: 'id', ignoreDuplicates: true }
          );

        if (profileError) {
          console.warn('Profile upsert warning (may be duplicate from trigger):', profileError.message);
        }

        // Send branded welcome email via Resend (non-blocking)
        if (data.user.email) {
          const confirmationUrl = 'https://chronomind.ai/auth/callback';
          fetch('/api/email/send-welcome', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              to: data.user.email,
              name: fullName,
              confirmationUrl,
            }),
          }).catch(() => {}); // Non-blocking, ignore errors
        }
      }

      router.refresh();
    } catch (error: any) {
      console.error('Sign up error:', error.message);
      throw error;
    }
  };

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;

      setUser(null);
      setUserProfile(null);
      setCurrentMembership(null);
      setCurrentOrganization(null);
      setAuthError(null);

      // Clear Sentry user context
      clearSentryUser();

      // Track logout event
      trackEvent('logout');

      router.push('/login');
    } catch (error: any) {
      console.error('Sign out error:', error.message);
      throw error;
    }
  };

  const refreshProfile = async () => {
    if (user) {
      await fetchUserProfile(user.id);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        userProfile,
        currentMembership,
        currentOrganization,
        loading,
        authError,
        signIn,
        signInWithGoogle,
        signUp,
        signOut,
        refreshProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
